import React from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { push } from 'connected-react-router';
import Autosuggest from 'react-autosuggest';
import {
  fetchSubmission,
  searchSubmissions,
  fetchForms,
  I18n,
  defineKqlQuery,
} from '@kineticdata/react';
import {
  selectServicesKapp,
  TimeAgo,
  Utils,
  C,
} from '@kineticdata/bundle-common';
import { Modal } from 'reactstrap';
import classNames from 'classnames';
import { debounce } from 'lodash-es';
import { actions } from '../../redux/modules/app';

const GUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const HANDLE_PATTERN = /^[0-9a-f]{6}$/i;

// Max number of suggestions to show
const PAGE_SIZE = 25;
const INLINE_PAGE_SIZE = 100;
// Minimum number of characters that must be typed to allow a search to execute
const MIN_SEARCH_LENGTH = 2;
const SERVICES_FORM_FIELDS = ['name', 'description', 'attributes[Keyword]'];
const REQUESTS_FORM_FIELDS = [
  `values[${C.FIELD_TICKET_NUMBERS}]`,
  `values[${C.FIELD_AGREEMENT}]`,
  `values[${C.FIELD_SERVER_NAMES}]`,
  `values[${C.FIELD_PROJECT}]`,
  `values[${C.FIELD_TRACKING_NUMBER}]`,
];

const SUGGESTION_MAPPERS = {
  submission: submission => ({
    id: submission.id,
    label: submission.label,
    path:
      submission.type === C.APPROVAL_FORM_TYPE
        ? `/kapps/${submission.form.kapp.slug}/approvals/approval/${
            submission.id
          }`
        : submission.type === C.ACTION_FORM_TYPE
          ? `/kapps/${submission.form.kapp.slug}/actions/action/${
              submission.id
            }`
          : `/kapps/${submission.form.kapp.slug}/submissions/${submission.id}`,
    data: submission,
    type: 'request',
    priority: 1,
  }),
  form: form => ({
    id: form.slug,
    label: form.name,
    path: `/kapps/${form.kapp.slug}/${
      form.type === C.PORTAL_PAGE_FORM_TYPE ? 'page' : 'forms'
    }/${form.slug}`,
    data: form,
    type: 'form',
    priority: 5,
  }),
  request: submission => ({
    id: submission.id,
    label: submission.label,
    path: `/kapps/${submission.form.kapp.slug}/submissions/${submission.id}`,
    data: submission,
    type: 'request',
    priority: 6,
  }),
};

const SUGGESTION_RENDERERS = {
  form: ({ label, data, favorites, toggleFavorite }) => (
    <div className="d-flex">
      <div className="icon align-self-center flex-shrink-0">
        <span className={`fa fa-${Utils.getIcon(data, 'folder')} fa-fw`} />
      </div>
      <div className="flex-grow-1">
        <div className="smallcaps">
          <I18n>{data.type}</I18n>
        </div>
        <div className="large">
          <I18n>{label}</I18n>
          {Utils.hasAttributeValue(
            data,
            C.FORM_ATTR_FUNDING_DEPENDENT,
            'True',
            true,
          ) && (
            <span
              className="badge badge-subtle badge-sm ml-1"
              title="Funding Dependent"
            >
              <span className="fa fa-dollar-sign" />
            </span>
          )}
          {!!Utils.getAttributeValue(data, C.FORM_ATTR_INTERNAL) && (
            <span
              className="badge badge-subtle badge-sm ml-1"
              title="Internal Service Offering"
            >
              <I18n>Internal</I18n>
            </span>
          )}
        </div>
        {data.description && (
          <div className="small text-muted text-truncate">
            {data.description}
          </div>
        )}
      </div>
      {favorites &&
        typeof toggleFavorite === 'function' && (
          <div className="icon align-self-center flex-shrink-0">
            <button
              className="btn btn-icon toggle-favorite"
              data-ignore-suggestion-click={true}
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                toggleFavorite(data.slug);
              }}
              title="Toggle Favorite"
            >
              <span
                className={classNames(
                  {
                    'fa text-primary': favorites.includes(data.slug),
                    'far text-dark': !favorites.includes(data.slug),
                  },
                  'fa-lg fa-heart m-0',
                )}
              />
            </button>
          </div>
        )}
    </div>
  ),
  request: ({ label, data }) => {
    const idList = [data.handle, ...(data.values[C.FIELD_TICKET_NUMBERS] || [])]
      .filter(Boolean)
      .join(' \u00b7 ');
    return (
      <div className="d-flex">
        <div className="icon align-self-center flex-shrink-0">
          <span className={`${C.ICON_REQUEST} fa-fw`} />
        </div>
        <div className="flex-grow-1">
          <div className="smallcaps">
            <I18n>Request</I18n>
            {data.form && (
              <>
                {' \u00b7 '}
                <I18n>{data.form.name}</I18n>
              </>
            )}
            {idList && (
              <>
                {' \u00b7 '}
                {idList}
              </>
            )}
          </div>
          <div className="large">
            <I18n>{label}</I18n>
          </div>
          <div className="small text-muted">
            <TimeAgo timestamp={data.submittedAt || data.createdAt} />
            {!!data.values[C.FIELD_SUMMARY] && (
              <>
                {' \u00b7 '}
                <I18n>{data.values[C.FIELD_SUMMARY]}</I18n>
              </>
            )}
          </div>
        </div>
      </div>
    );
  },
  submission: ({ label, data }) => {
    const idList = [data.handle, ...(data.values[C.FIELD_TICKET_NUMBERS] || [])]
      .filter(Boolean)
      .join(' \u00b7 ');
    return (
      <div className="d-flex">
        <div className="icon align-self-center flex-shrink-0">
          <span className={`${C.ICON_REQUEST} fa-fw`} />
        </div>
        <div className="flex-grow-1">
          <div className="smallcaps">
            <I18n>Request</I18n>
            {data.form && (
              <>
                {' \u00b7 '}
                <I18n>{data.form.name}</I18n>
              </>
            )}
            {idList && (
              <>
                {' \u00b7 '}
                {idList}
              </>
            )}
          </div>
          <div className="large">
            <I18n>{label}</I18n>
          </div>
          <div className="small text-muted">
            <TimeAgo timestamp={data.submittedAt || data.createdAt} />
            {!!data.values[C.FIELD_SUMMARY] && (
              <>
                {' \u00b7 '}
                <I18n>{data.values[C.FIELD_SUMMARY]}</I18n>
              </>
            )}
          </div>
        </div>
      </div>
    );
  },
};

// Helper function for building user search q parameter
const buildFormQuery = (value, isSpaceAdmin) => {
  const searcher = defineKqlQuery();
  // Add static query parts
  if (!isSpaceAdmin) {
    searcher.in('type', 'type');
  }
  searcher.in('status', 'statuses');
  // Add value query parts
  searcher.or();
  SERVICES_FORM_FIELDS.forEach(field => searcher.matches(field, 'value'));
  searcher.end();
  return searcher.end()({
    type: [C.REQUEST_FORM_TYPE, C.PORTAL_PAGE_FORM_TYPE],
    statuses: C.REQUEST_FORM_STATUSES,
    value,
  });
};

// Helper function for building request search q parameter
const buildRequestQuery = (value, profile) => {
  const searcher = defineKqlQuery();
  // Add static query parts
  searcher.equals('type', 'type').in('coreState', 'coreState');
  // Add agreements to query for non super users
  if (!profile.superUser && !profile.superUserRO) {
    searcher.in(`values[${C.FIELD_ASSIGNED_TEAM}]`, 'agreements');
  }
  // Add value query parts
  searcher.or();
  REQUESTS_FORM_FIELDS.forEach(field => searcher.equals(field, 'value'));
  searcher.end();
  return searcher.end()({
    type: C.REQUEST_FORM_TYPE,
    coreState: [C.CORE_STATE_SUBMITTED, C.CORE_STATE_CLOSED],
    agreements: profile.agreements,
    value,
  });
};

class SearchBarComponent extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      // Current value
      value: props.value || '',
      // Current results/suggestions
      suggestions: [],
      // Should the search bar be visible
      open: /*!props.inline && */ !props.modal,
      // Should the suggestions/status be visible
      active: !!props.inline,
      // Is query executing
      loading: false,
      // Did query error
      error: null,
      // Did query return too many results
      more: false,
      // Did query return no results
      empty: false,
      // Is the enterd value too short
      short: !props.value || props.value.length < MIN_SEARCH_LENGTH,
    };
    this.inline = props.inline;
    this.pageSize = props.inline ? INLINE_PAGE_SIZE : PAGE_SIZE;
    this.autosuggestRef = React.createRef();
    // Keep track of when the last query was made so we don't update the search
    // bar with outdated results if multiple queries are made simultaneously
    this.lastSearchTimestamp = null;
  }

  componentDidMount() {
    if (this.state.value && !this.state.short) {
      this.setState({ loading: true });
      this.search(this.state.value, this.onSuggestionsFetchCompleted);
    }
  }

  componentDidUpdate(prevProps) {
    if (
      !!this.props.value &&
      this.props.value !== prevProps.value &&
      this.props.value !== this.state.value
    ) {
      const short = this.props.value.length < MIN_SEARCH_LENGTH;
      this.setState({
        value: this.props.value,
        suggestions: [],
        loading: !short,
        error: null,
        more: false,
        empty: false,
        short,
      });
      if (!short) {
        this.search(this.props.value, this.onSuggestionsFetchCompleted);
      }
    }

    // If modal state changes, set open state to the correct default for the view
    if (this.props.modal !== prevProps.modal) {
      this.setState({ open: !this.props.modal });
    }
  }

  /**
   * Returns an array of dataSources that should be searched for the given value
   * Each dataSource is an object with the following properties:
   *   fn: API function to call
   *   params: Parameter object to pass to the API function
   *   transform: Function that transforms the API results into the expected
   *       suggestion format
   *   then: Optional function to call after the query completes
   */
  generateDataSources = value => {
    // Check if value matches a handle or guid
    const isHandle = HANDLE_PATTERN.test(value);
    const isGuid = GUID_PATTERN.test(value);
    const authenticated = !!this.props.profile;

    return [
      // Search for services forms if value doesn't match a guid
      authenticated &&
        !isGuid &&
        !!this.props.servicesKapp && {
          fn: fetchForms,
          params: {
            kappSlug: this.props.servicesKapp.slug,
            q: buildFormQuery(value, this.props.profile.spaceAdmin),
            limit: this.pageSize,
            include: 'details,categorizations,attributesMap,kapp',
          },
          transform: ({ forms, error, nextPageToken }) => ({
            suggestions: forms
              ? forms
                  .filter(
                    form =>
                      !Utils.hasAttributeValue(
                        form,
                        C.FORM_ATTR_EXCLUDE_FROM_SEARCH,
                        'True',
                        true,
                      ),
                  )
                  .map(SUGGESTION_MAPPERS.form)
              : [],
            error,
            more: !!nextPageToken,
          }),
        },
      // Search for specific submission if value is a guid
      authenticated &&
        isGuid && {
          fn: fetchSubmission,
          params: {
            id: value,
            include: `details,form,form.kapp,values[${
              C.FIELD_SUMMARY
            }],values[${C.FIELD_TICKET_NUMBERS}]`,
          },
          transform: ({ submission, error }) => ({
            suggestions: submission
              ? [SUGGESTION_MAPPERS.submission(submission)]
              : [],
            error: error && error.statusCode === 404 ? null : error,
            more: false,
          }),
        },
      // Search for requests if value is a handle
      authenticated &&
        isHandle &&
        !!this.props.servicesKapp && {
          fn: searchSubmissions,
          params: {
            search: {
              q: `handle = "${value}"`,
              include: [
                'details',
                'form',
                'form.kapp',
                `values[${C.FIELD_SUMMARY}]`,
                `values[${C.FIELD_TICKET_NUMBERS}]`,
              ],
            },
            kapp: this.props.servicesKapp.slug,
            limit: this.pageSize,
          },
          transform: ({ submissions, error, nextPageToken }) => ({
            suggestions: submissions
              ? submissions.map(SUGGESTION_MAPPERS.submission)
              : [],
            error,
            more: !!nextPageToken,
          }),
        },
      // Search for requests if value is not a guid
      authenticated &&
        !isGuid &&
        !!this.props.servicesKapp && {
          fn: searchSubmissions,
          params: {
            search: {
              q: buildRequestQuery(value, this.props.profile),
              include: [
                'details',
                'form',
                'form.kapp',
                `values[${C.FIELD_SUMMARY}]`,
                `values[${C.FIELD_TICKET_NUMBERS}]`,
              ],
            },
            kapp: this.props.servicesKapp.slug,
            limit: this.pageSize,
          },
          transform: ({ submissions, error, nextPageToken }) => ({
            suggestions: submissions
              ? submissions.map(SUGGESTION_MAPPERS.request)
              : [],
            error,
            more: !!nextPageToken,
          }),
        },
    ].filter(Boolean);
  };

  toggle = debounce(open => {
    if ((open && !this.state.open) || (!open && this.state.open)) {
      this.setState({
        open: this.props.modal ? open : true,
        value: '',
      });
    }
  }, 200);

  toggleOpen = () => {
    this.toggle(true);
  };

  toggleClose = () => {
    this.toggle(false);
  };

  focusInput = () => {
    if (this.state.open && this.autosuggestRef.current) {
      this.autosuggestRef.current.input.focus();
    }
  };

  onBtnFocus = () => {
    if (this.state.open) {
      this.toggleOpen();
    }
  };

  onBtnBlur = () => {
    this.toggleClose();
  };

  // Set state value on change
  onChange = (event, { newValue, method }) => {
    this.setState({ value: newValue });
    if (typeof this.props.onChange === 'function') {
      this.props.onChange(newValue, method);
    }
  };

  // Open suggestions container on focus
  onFocus = () => {
    // console.log('onFocus', { ...this.state });
    if (!this.props.modal && !this.inline) {
      this.setState({ active: true });
      this.toggleOpen();
    }
  };

  // Close suggestions container on blur
  onBlur = () => {
    // console.log('onBlur', { ...this.state });
    if (!this.props.modal && !this.inline) {
      this.setState({ active: false });
      this.toggleClose();
    }
  };

  // Initiate search on press of Enter key if query is valid
  onKeyDown = event => {
    if (event.keyCode === 13) {
      // console.log(
      //   'onKeyDown',
      //   { ...this.state },
      //   event.keyCode,
      //   this.state.active,
      // );
      if (!this.state.short && !this.props.isFullView) {
        this.goToAllResults();
      }
    }
  };

  // When a suggestion is selected, close suggestions container and take
  // appropriate action based on type of suggestion
  onSuggestionSelected = (event, { suggestion }) => {
    if (suggestion) {
      this.navigate(suggestion.path);
      if (this.autosuggestRef.current) {
        const input = this.autosuggestRef.current.input;
        window.setTimeout(() => input.blur(), 100);
      }
    }
  };

  navigate = debounce(
    (...args) => {
      this.props.push(...args);
    },
    200,
    { leading: true, trailing: false },
  );

  goToAllResults = () => {
    this.navigate(`/search/${this.state.value}`);
    if (this.autosuggestRef.current) {
      const input = this.autosuggestRef.current.input;
      window.setTimeout(() => input.blur(), 100);
    }
  };

  // Fetches the data to show as suggestions.
  retrieveSuggestions = timestamp => (value, callback) => {
    const dataSources = this.generateDataSources(value);
    // Fetch forms and requests
    Promise.all(dataSources.map(ds => ds.fn(ds.params))).then(rawResults => {
      // If another retrieve was fired before this one finished, don't process
      // the results of this one.
      if (this.lastSearchTimestamp !== timestamp) {
        return;
      }
      // Call any then functions from the dataSources
      rawResults.forEach((r, i) => {
        if (typeof dataSources[i].then === 'function') {
          dataSources[i].then(r);
        }
      });
      // Transform each result
      const results = rawResults.map((r, i) => dataSources[i].transform(r));
      // Merge all suggestions together
      const suggestions = results
        .flatMap(r => r.suggestions || [])
        .sort(
          (a, b) =>
            a.priority === b.priority
              ? a.label > b.label
                ? 1
                : a.label < b.label
                  ? -1
                  : 0
              : (a.priority || 99) - (b.priority || 99),
        )
        .slice(0, this.pageSize);
      // Call the callback function with the data
      callback({
        suggestions,
        error: results.map(r => r.error).filter(Boolean)[0],
        more: results.some(r => r.more) || suggestions.length > this.pageSize,
      });
    });
  };

  search = debounce((...args) => {
    const timestamp = new Date().getTime();
    this.lastSearchTimestamp = timestamp;
    this.retrieveSuggestions(timestamp)(...args);
  }, 150);

  // When the value changes, validate the query and reset state. This will hide
  // suggestions and re-show the help text if a user modifies their query.
  onSuggestionsFetchRequested = ({ value, reason }) => {
    // console.log('onSuggestionsFetchRequested', value, this.state.value, reason);

    if (reason !== 'input-focused' || (!this.props.modal && !this.inline)) {
      const short = value.length < MIN_SEARCH_LENGTH;
      this.setState({
        suggestions: [],
        loading: !short,
        error: null,
        more: false,
        empty: false,
        short,
      });
      if (!short) {
        this.search(value, this.onSuggestionsFetchCompleted);
      }
    }
  };

  // When a query is completed, update state with the suggestions and statuses
  onSuggestionsFetchCompleted = ({ suggestions = [], error, more }) => {
    // console.log('onSuggestionsFetchCompleted', { ...this.state }, suggestions);
    this.setState({
      suggestions,
      loading: false,
      error,
      more,
      empty: suggestions && suggestions.length === 0,
    });
  };

  // When the search bar is blurred or a suggestion is selected, reset state
  onSuggestionsClearRequested = (...args) => {
    // console.log('onSuggestionsClearRequested', args);
    if (!this.props.modal && !this.inline) {
      this.setState({
        suggestions: [],
        loading: false,
        error: null,
        more: false,
        empty: false,
        short: true,
      });
    }
  };

  // Renders a single suggestion
  renderSuggestion = (suggestion, { isHighlighted }) => {
    const Component = SUGGESTION_RENDERERS[suggestion.type];
    return (
      <Link
        to={suggestion.path}
        className={classNames('suggestion', {
          active: isHighlighted,
        })}
        onClick={e => e.preventDefault()}
      >
        {Component && (
          <Component
            {...suggestion}
            favorites={this.props.favorites}
            toggleFavorite={
              this.inline ? this.props.toggleFavoriteRequest : undefined
            }
          />
        )}
      </Link>
    );
  };

  // Renders the suggestions wrapper and any status/error messages
  renderSuggestionsContainer = ({ containerProps, children }) =>
    (this.props.modal || this.inline || this.state.active) && (
      <div
        {...containerProps}
        className={classNames('suggestions', {
          open: this.state.open,
          inline: this.props.inline,
        })}
      >
        {this.state.loading ? (
          <div className="status info">
            <I18n>Searching...</I18n>
          </div>
        ) : this.state.short ? (
          <div className="status info">
            <I18n
              render={translate =>
                translate(
                  `Enter at least %d character${
                    MIN_SEARCH_LENGTH !== 1 ? 's' : ''
                  } to search through the catalog, or enter a confirmation number, incident, change or server name.`,
                ).replace('%d', MIN_SEARCH_LENGTH)
              }
            />
          </div>
        ) : this.state.error ? (
          <div className="status warning">
            <I18n>There was an error fetching some results.</I18n>
          </div>
        ) : this.state.empty ? (
          <div className="status warning">
            <I18n>No matching results</I18n>
          </div>
        ) : this.state.more ? (
          <div className="status warning">
            <I18n
              render={translate =>
                translate(
                  'Too many results, first %d shown. Please refine your search.',
                ).replace('%d', this.pageSize)
              }
            />
          </div>
        ) : null}
        {children}
      </div>
    );

  renderInputComponent = inputProps => (
    <input aria-label="Search Bar" {...inputProps} />
  );

  render() {
    const { open, value, suggestions } = this.state;

    const autosuggest = (
      <I18n
        render={translate => (
          <Autosuggest
            getSuggestionValue={suggestion =>
              suggestion ? suggestion.label : ''
            }
            inputProps={{
              value: value,
              onChange: this.onChange,
              onBlur: this.onBlur,
              onFocus: this.onFocus,
              onKeyDown: this.onKeyDown,
              placeholder: translate(
                'What are you looking for? (i.e. form name, confirmation #, incident, change or server name)',
              ),
              autoFocus: this.props.autoFocus,
            }}
            onSuggestionHighlighted={this.onHighlight}
            onSuggestionSelected={this.onSuggestionSelected}
            onSuggestionsClearRequested={this.onSuggestionsClearRequested}
            onSuggestionsFetchRequested={this.onSuggestionsFetchRequested}
            renderSuggestion={this.renderSuggestion}
            renderSuggestionsContainer={this.renderSuggestionsContainer}
            renderInputComponent={this.renderInputComponent}
            alwaysRenderSuggestions={this.props.modal || this.inline}
            suggestions={suggestions}
            ref={this.autosuggestRef}
          />
        )}
      />
    );

    return this.inline ? (
      <div className="app-search--inline">
        <div className="kinetic-typeahead">
          <button
            className="search"
            onClick={this.focusInput}
            aria-label="Focus Search Bar"
          >
            <span className="fa fa-lg fa-search" />
          </button>
          {autosuggest}
        </div>
      </div>
    ) : this.props.modal ? (
      <>
        <button
          className="search"
          onClick={this.toggleOpen}
          aria-label="Open Search Bar"
        >
          <span className="fa fa-lg fa-search" />
        </button>
        <Modal
          className="app-search--modal"
          size="lg"
          isOpen={this.state.open}
          toggle={this.toggleClose}
        >
          <div className="kinetic-typeahead">
            <button
              className="search"
              onClick={this.focusInput}
              aria-label="Focus Search Bar"
            >
              <span className="fa fa-lg fa-search" />
            </button>
            {autosuggest}
            {value && (
              <button className="goto-search" onClick={this.goToAllResults}>
                <span className="fa fa-lg fa-arrow-right" />
              </button>
            )}
            <span className="divider" />
            <button className="close-search" onClick={this.toggleClose}>
              <span className="fa fa-lg fa-times" />
            </button>
          </div>
        </Modal>
      </>
    ) : (
      <div
        className={classNames('kinetic-typeahead', this.props.className, {
          open,
        })}
      >
        <button
          className="search"
          onClick={!open ? this.toggleOpen : this.focusInput}
          onFocus={this.onBtnFocus}
          onBlur={this.onBtnBlur}
          aria-label={!open ? 'Open Search Bar' : 'Focus Search Bar'}
        >
          <span className="fa fa-lg fa-search" />
        </button>
        {open && autosuggest}
        {value &&
          open && (
            <button
              className="goto-search"
              onClick={this.goToAllResults}
              onFocus={this.onBtnFocus}
              onBlur={this.onBtnBlur}
            >
              <span className="fa fa-lg fa-arrow-right" />
            </button>
          )}
      </div>
    );
  }
}

export const SearchBar = connect(
  state => ({
    servicesKapp: selectServicesKapp(state),
    profile: state.app.profile,
    favorites: Utils.getProfileAttributeValues(
      state.app.profile,
      C.ATTRIBUTE_USER_PROFILE_FAVORITES,
    ),
  }),
  { push, toggleFavoriteRequest: actions.toggleFavoriteRequest },
)(SearchBarComponent);
